import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  computeObjectContainImageLayout,
  cropBoxesEqual,
  displayRectToNormalized,
  FULL_FRAME_CROP_BOX,
  normalizedToDisplayRect,
} from '@/lib/imageStoryCrop';

const HANDLE = 10;
const MIN_DISP = 28;

function clampRectToSheet(rect, layout) {
  const { ox, oy, dispW, dispH } = layout;
  const maxW = dispW;
  const maxH = dispH;
  let { left, top, width, height } = rect;

  width = Math.max(MIN_DISP, width);
  height = Math.max(MIN_DISP, height);

  left = Math.max(ox, Math.min(ox + maxW - width, left));
  top = Math.max(oy, Math.min(oy + maxH - height, top));

  width = Math.min(width, ox + maxW - left);
  height = Math.min(height, oy + maxH - top);

  return { left, top, width, height };
}

export const ImageStoryBoundingBoxEditor = forwardRef(function ImageStoryBoundingBoxEditor(
  {
    imageSrc,
    scenes,
    onSceneCropCommit,
    onSceneCropFinalize,
    onSceneDoubleClick,
    onDirtyChange,
    disabled = false,
    className = '',
  },
  ref,
) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const layoutRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [draftByClientId, setDraftByClientId] = useState({});
  const dragRef = useRef(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    const next = computeObjectContainImageLayout(img, container);
    if (!next) return;
    layoutRef.current = next;
    setLayout(next);
  }, []);

  useEffect(() => {
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);
    if (ro && imgRef.current) ro.observe(imgRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, imageSrc, scenes]);

  useEffect(() => {
    setDraftByClientId({});
  }, [imageSrc, scenes]);

  const resolveSceneCropNorm = useCallback(
    (scene) => {
      const clientId = scene.client_id || `segment-${scene.segment_number}`;
      const draftRect = draftByClientId[clientId];
      if (draftRect && layoutRef.current) {
        return displayRectToNormalized(draftRect, layoutRef.current);
      }
      return scene.crop_box || FULL_FRAME_CROP_BOX;
    },
    [draftByClientId],
  );

  const boxes = useMemo(() => {
    if (!layout || !Array.isArray(scenes)) return [];
    return scenes
      .filter((s) => s?.segment_number != null && String(s.segment_number).trim() !== '')
      .map((scene) => {
        const clientId = scene.client_id || `segment-${scene.segment_number}`;
        const cropNorm = scene.crop_box || FULL_FRAME_CROP_BOX;
        const draft = draftByClientId[clientId];
        const base = normalizedToDisplayRect(cropNorm, layout);
        const rect = draft || base;
        return {
          scene,
          clientId,
          rect,
          hue: ((Number(scene.segment_number) - 1) * 47) % 360,
        };
      })
      .filter((b) => b.rect && b.rect.width > 0 && b.rect.height > 0);
  }, [scenes, layout, draftByClientId]);

  const hasPendingCropChanges = useMemo(() => {
    if (!scenes?.length) return false;
    return scenes.some((scene) => {
      const clientId = scene.client_id || `segment-${scene.segment_number}`;
      const draftRect = draftByClientId[clientId];
      if (!draftRect || !layout) return false;
      const draftNorm = displayRectToNormalized(draftRect, layout);
      const saved = scene.crop_box || FULL_FRAME_CROP_BOX;
      return !cropBoxesEqual(draftNorm, saved);
    });
  }, [draftByClientId, layout, scenes]);

  useEffect(() => {
    onDirtyChange?.(hasPendingCropChanges);
  }, [hasPendingCropChanges, onDirtyChange]);

  const commitDraft = useCallback(
    async (clientId, segmentNumber, rect) => {
      const lay = layoutRef.current;
      if (!lay || !onSceneCropCommit) return;
      const norm = displayRectToNormalized(rect, lay);
      await onSceneCropCommit(segmentNumber, norm, clientId);
      setDraftByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
    },
    [onSceneCropCommit],
  );

  const getSceneCropUpdates = useCallback(() => {
    return (scenes || [])
      .filter((s) => s?.segment_number != null && String(s.segment_number).trim() !== '')
      .map((scene) => {
        const sn = Number(scene.segment_number);
        const clientId = scene.client_id || `segment-${sn}`;
        const cropNorm = resolveSceneCropNorm(scene);
        return { segmentNumber: sn, clientId, cropNorm };
      });
  }, [resolveSceneCropNorm, scenes]);

  const commitAllPendingCrops = useCallback(async () => {
    if (!onSceneCropCommit || !layoutRef.current) return 0;
    let count = 0;
    for (const { scene, clientId } of boxes) {
      const draftRect = draftByClientId[clientId];
      if (!draftRect) continue;
      const sn = Number(scene.segment_number);
      const saved = scene.crop_box || FULL_FRAME_CROP_BOX;
      const draftNorm = displayRectToNormalized(draftRect, layoutRef.current);
      if (cropBoxesEqual(draftNorm, saved) && scene.image_url) continue;
      // eslint-disable-next-line no-await-in-loop
      await onSceneCropCommit(sn, draftNorm, clientId);
      count += 1;
      setDraftByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
    }
    return count;
  }, [boxes, draftByClientId, onSceneCropCommit]);

  const clearPendingDrafts = useCallback(() => {
    setDraftByClientId({});
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      hasPendingCropChanges: () => hasPendingCropChanges,
      getSceneCropUpdates,
      commitAllPendingCrops,
      clearPendingDrafts,
    }),
    [clearPendingDrafts, commitAllPendingCrops, getSceneCropUpdates, hasPendingCropChanges],
  );

  useEffect(() => () => {
    dragRef.current = null;
  }, []);

  const beginDrag = (ev, clientId, segmentNumber, mode, rect) => {
    if (disabled || !layout) return;
    ev.preventDefault();
    ev.stopPropagation();
    ev.target?.setPointerCapture?.(ev.pointerId);

    const startNorm = displayRectToNormalized(rect, layout);

    dragRef.current = {
      clientId,
      segmentNumber,
      mode,
      startClientX: ev.clientX,
      startClientY: ev.clientY,
      startRect: { ...rect },
      currentRect: { ...rect },
      startNorm,
    };

    const onPointerMove = (moveEv) => {
      const d = dragRef.current;
      if (!d || !layout) return;

      const dx = moveEv.clientX - d.startClientX;
      const dy = moveEv.clientY - d.startClientY;
      let next = { ...d.startRect };

      if (d.mode === 'move') {
        next.left += dx;
        next.top += dy;
      } else if (d.mode === 'nw') {
        next.left += dx;
        next.top += dy;
        next.width -= dx;
        next.height -= dy;
      } else if (d.mode === 'ne') {
        next.top += dy;
        next.width += dx;
        next.height -= dy;
      } else if (d.mode === 'sw') {
        next.left += dx;
        next.width -= dx;
        next.height += dy;
      } else if (d.mode === 'se') {
        next.width += dx;
        next.height += dy;
      }

      next = clampRectToSheet(next, layout);
      d.currentRect = next;
      setDraftByClientId((prev) => ({
        ...prev,
        [d.clientId]: next,
      }));
    };

    const onPointerUp = async () => {
      const d = dragRef.current;
      dragRef.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      if (!d || !layout) return;
      setDraftByClientId((prev) => ({
        ...prev,
        [d.clientId]: d.currentRect,
      }));
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  const colors = boxes.map((b) => `hsla(${b.hue}, 75%, 48%, 0.95)`);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <img
        ref={imgRef}
        src={imageSrc}
        alt="Storyboard image — drag highlighted regions to adjust scene crops"
        draggable={false}
        className="w-full max-h-[520px] object-contain pointer-events-none select-none mx-auto block"
        onLoad={measure}
      />

      {layout && boxes.length > 0 ? (
        <div className="absolute inset-0 pointer-events-none">
          {boxes.map(({ scene, clientId, rect }, idx) => {
            const sn = scene.segment_number;
            const border = colors[idx] || '#6366f1';
            const hl = `hsla(${((Number(scene.segment_number) - 1) * 47) % 360}, 75%, 48%, 0.22)`;

            return (
              <div
                key={clientId}
                className="absolute pointer-events-auto"
                style={{
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                  boxShadow: `inset 0 0 0 2px ${border}, 0 0 0 1px rgba(255,255,255,0.85)`,
                  backgroundColor: hl,
                  cursor: disabled ? 'default' : 'move',
                  touchAction: 'none',
                }}
                onPointerDown={(e) => !disabled && beginDrag(e, clientId, sn, 'move', rect)}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (!disabled && onSceneDoubleClick) onSceneDoubleClick(scene);
                }}
                role="presentation"
              >
                <span className="absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-wide text-[#1E293B] bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-slate-200 pointer-events-none">
                  Scene {sn}
                </span>

                {!disabled ? (
                  <>
                    {[
                      { mode: 'nw', style: { left: -HANDLE / 2, top: -HANDLE / 2, cursor: 'nwse-resize' } },
                      { mode: 'ne', style: { right: -HANDLE / 2, top: -HANDLE / 2, cursor: 'nesw-resize' } },
                      { mode: 'sw', style: { left: -HANDLE / 2, bottom: -HANDLE / 2, cursor: 'nesw-resize' } },
                      { mode: 'se', style: { right: -HANDLE / 2, bottom: -HANDLE / 2, cursor: 'nwse-resize' } },
                    ].map(({ mode, style }) => (
                      <button
                        key={mode}
                        type="button"
                        aria-label={`Resize scene ${sn} ${mode}`}
                        className="absolute w-2.5 h-2.5 rounded-full bg-white border-2 border-[#4338CA] shadow-md z-10"
                        style={style}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          beginDrag(e, clientId, sn, mode, rect);
                        }}
                      />
                    ))}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});
